import PropTypes from 'prop-types';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { formatAnswer } from './schema';

function EditableField({ field, value, onChange, invalid, describedBy }) {
  const common = { id: field.id, 'aria-invalid': invalid, 'aria-describedby': describedBy };
  if (field.type === 'short_text') return <Input {...common} value={value || ''} onChange={event => onChange(field.id, event.target.value)} />;
  if (field.type === 'long_text') return <Textarea {...common} value={value || ''} onChange={event => onChange(field.id, event.target.value)} />;
  if (field.type === 'number') return <Input {...common} type="number" value={value ?? ''} min={field.validation?.min} max={field.validation?.max}
    onChange={event => onChange(field.id, event.target.value === '' ? null : Number(event.target.value))} />;
  if (field.type === 'date') return <Input {...common} type="date" value={value || ''} min={field.validation?.earliest} max={field.validation?.latest}
    onChange={event => onChange(field.id, event.target.value)} />;
  if (field.type === 'radio') return (
    <RadioGroup value={value || ''} onValueChange={next => onChange(field.id, next)} aria-invalid={invalid} aria-describedby={describedBy}>
      {field.options.map(option => <div key={option.id} className="flex items-center gap-2">
        <RadioGroupItem id={`${field.id}-${option.id}`} value={option.id} />
        <Label htmlFor={`${field.id}-${option.id}`}>{option.label}</Label>
      </div>)}
    </RadioGroup>
  );
  if (field.type === 'checkbox') return <div role="group" aria-labelledby={`${field.id}-label`} aria-invalid={invalid} aria-describedby={describedBy}>
    {field.options.map(option => {
      const selected = value || [];
      return <div key={option.id} className="flex items-center gap-2">
        <Checkbox id={`${field.id}-${option.id}`} checked={selected.includes(option.id)} onCheckedChange={checked =>
          onChange(field.id, checked ? [...new Set([...selected, option.id])] : selected.filter(item => item !== option.id))} />
        <Label htmlFor={`${field.id}-${option.id}`}>{option.label}</Label>
      </div>;
    })}
  </div>;
  if (field.type === 'select') return <Select value={value || ''} onValueChange={next => onChange(field.id, next)}>
    <SelectTrigger id={field.id} aria-invalid={invalid} aria-describedby={describedBy}><SelectValue placeholder="Select an option" /></SelectTrigger>
    <SelectContent>{field.options.map(option => <SelectItem key={option.id} value={option.id}>{option.label}</SelectItem>)}</SelectContent>
  </Select>;
  return <p role="alert">Unsupported field type: {field.type}</p>;
}

export default function FormRenderer({ schema, answers, onChange = () => {}, readOnly = false, errors = [] }) {
  const errorsByField = new Map(errors.filter(item => item.fieldId).map(item => [item.fieldId, item.message]));
  return <div className="space-y-8">
    {schema.sections.map(section => <section id={section.id} key={section.id} aria-labelledby={`${section.id}-title`} className="space-y-5">
      <div><h3 id={`${section.id}-title`} className="text-lg font-semibold">{section.title}</h3>
        {section.description && <p className="text-sm text-slate-500">{section.description}</p>}</div>
      {section.fields.map(field => {
        const message = errorsByField.get(field.id);
        const describedBy = [field.help_text && `${field.id}-help`, message && `${field.id}-error`].filter(Boolean).join(' ') || undefined;
        return <div key={field.id} className="space-y-2">
          <Label id={`${field.id}-label`} htmlFor={field.id}>{field.label}{field.required && <span aria-hidden="true"> *</span>}</Label>
          {readOnly ? <p className="whitespace-pre-wrap text-sm text-slate-700">{formatAnswer(field, answers[field.id]) || '—'}</p>
            : <EditableField field={field} value={answers[field.id]} onChange={onChange} invalid={Boolean(message)} describedBy={describedBy} />}
          {field.help_text && <p id={`${field.id}-help`} className="text-xs text-slate-500">{field.help_text}</p>}
          {message && <p id={`${field.id}-error`} role="alert" className="text-sm text-red-600">{message}</p>}
        </div>;
      })}
    </section>)}
  </div>;
}

EditableField.propTypes = { field: PropTypes.object.isRequired, value: PropTypes.any, onChange: PropTypes.func.isRequired, invalid: PropTypes.bool, describedBy: PropTypes.string };
FormRenderer.propTypes = { schema: PropTypes.object.isRequired, answers: PropTypes.object.isRequired, onChange: PropTypes.func, readOnly: PropTypes.bool, errors: PropTypes.array };
